/**
 * Lightweight in-process event bus for chat read-state propagation.
 *
 * Why this exists:
 *  - The mark_as_read endpoints persist `last_read` server-side correctly,
 *    but Activity → Messages and the BottomNav badge only re-fetch on a
 *    timer (8s and 30s respectively). That leaves a stale red badge for
 *    several seconds after the user reads a chat — which the user reads as
 *    "unread badges aren't clearing".
 *  - Rather than thread refs through props or pull in a state library,
 *    we dispatch a CustomEvent on `window` whenever a chat is read. All
 *    surfaces that show unread badges listen and re-fetch immediately
 *    (and optimistically zero the local row before the refetch lands).
 */

const EVT = 'chat:read';

/**
 * Notify all listeners that a chat has been marked read.
 *
 * @param {{ type: 'plan'|'group'|'dm', id: string }} chatRef
 */
export function emitChatRead(chatRef) {
  try {
    window.dispatchEvent(new CustomEvent(EVT, { detail: chatRef }));
  } catch { /* SSR — no-op */ }
}

/**
 * Subscribe to chat-read events. Returns an unsubscribe fn.
 * Handler receives the chatRef detail (or undefined if it was a global ping).
 */
export function onChatRead(handler) {
  const wrapped = (e) => handler(e?.detail);
  window.addEventListener(EVT, wrapped);
  return () => window.removeEventListener(EVT, wrapped);
}
